// store/companySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as companyAPI from '../api/companyAPI';
import { UserType } from './userSlice'; // Import from same directory

export interface Company {
  id: number;
  name: string;
  created_at: string;
}

interface CompanyState {
  data: Company[];
  loading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  data: [],
  loading: false,
  error: null
};

// Helper: extract backend error safely with proper typing
function extractError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    // Axios error structure
    const axiosError = err as {
      response?: {
        data?: {
          detail?: string | Array<{ msg: string }>;
        };
      };
      message?: string;
    };

    if (axiosError.response?.data?.detail) {
      const detail = axiosError.response.data.detail;

      if (typeof detail === 'string') {
        return detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        return detail[0].msg || 'Validation error';
      }
    }

    // Generic error message
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  return 'Unexpected error occurred';
}

// Fetch Companies - FIXED: Match the parameter type with companyAPI.getCompanies
export const fetchCompanies = createAsyncThunk<
  Company[],
  { skip?: number; limit?: number } | undefined, // Use undefined instead of void
  { rejectValue: string }
>('companies/fetchCompanies', async (params, { rejectWithValue }) => {
  try {
    const res = await companyAPI.getCompanies(params);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Create Company
export const addCompany = createAsyncThunk<
  Company,
  companyAPI.CreateCompanyPayload,
  { rejectValue: string }
>('companies/addCompany', async (newCompany, { rejectWithValue }) => {
  try {
    const res = await companyAPI.createCompany(newCompany);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Update Company
export const editCompany = createAsyncThunk<
  Company,
  { id: number; companyData: companyAPI.UpdateCompanyPayload },
  { rejectValue: string }
>('companies/editCompany', async ({ id, companyData }, { rejectWithValue }) => {
  try {
    const res = await companyAPI.updateCompany(id, companyData);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Delete Company
export const deleteCompany = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('companies/deleteCompany', async (id, { rejectWithValue }) => {
  try {
    await companyAPI.deleteCompany(id);
    return id;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Get Company Users
export const fetchCompanyUsers = createAsyncThunk<
  UserType[],
  number,
  { rejectValue: string }
>('companies/fetchCompanyUsers', async (companyId, { rejectWithValue }) => {
  try {
    const res = await companyAPI.getCompanyUsers(companyId);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

const companySlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    resetCompanies: state => {
      state.data = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // ---------------- Fetch Companies ----------------
      .addCase(fetchCompanies.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCompanies.fulfilled,
        (state, action: PayloadAction<Company[]>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch companies';
        state.loading = false;
      })

      // ---------------- Add Company ----------------
      .addCase(addCompany.pending, state => {
        state.error = null;
      })
      .addCase(
        addCompany.fulfilled,
        (state, action: PayloadAction<Company>) => {
          state.data.push(action.payload);
          state.error = null;
        }
      )
      .addCase(addCompany.rejected, (state, action) => {
        state.error = action.payload || 'Failed to create company';
      })

      // ---------------- Edit Company ----------------
      .addCase(editCompany.pending, state => {
        state.error = null;
      })
      .addCase(
        editCompany.fulfilled,
        (state, action: PayloadAction<Company>) => {
          const index = state.data.findIndex(c => c.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(editCompany.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update company';
      })

      // ---------------- Delete Company ----------------
      .addCase(deleteCompany.pending, state => {
        state.error = null;
      })
      .addCase(
        deleteCompany.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.data = state.data.filter(c => c.id !== action.payload);
          state.error = null;
        }
      )
      .addCase(deleteCompany.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete company';
      })

      // ---------------- Fetch Company Users ----------------
      .addCase(fetchCompanyUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUsers.fulfilled, state => {
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCompanyUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch company users';
        state.loading = false;
      });
  }
});

export const { clearError, resetCompanies } = companySlice.actions;
export default companySlice.reducer;
